import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Avatar, Message } from "@chatscope/chat-ui-kit-react";
import { KAHUN_AVATAR_SRC, RAG_ABSOLUTE_PATH } from "../styles/Theme";
import {
  AnswerFooter,
  FooterHandle,
} from "fe-web-components/references/AnswerFooter";
import { safeCopyToClipboard } from "fe-common/utils/safeCopyToClipboard";
import { createQuestionId, GuidelineData } from "../context/sessionStore";
import useSessionStore from "../context/useSessionStore";
import { SuggestedQuestion } from "./SuggestedQuestion";
import {
  ExchangeFeedback,
  Guideline,
  PredefinedInsight,
} from "../graphql/types/graphql";
import { ThreeDotsLoader } from "./UI/ThreeDotsLoader";
import { useEventReportedContext } from "../analytics/EventReporterContext";
import { MIX_PANEL_EVENTS } from "../analytics/eventReporter";
import { MemoizedGuidelineIndicator } from "fe-web-components/guidelines/GuidelineIndicator";
import { isRtl, replaceHtmlEntities } from "../utils";
import { FeedbackInput } from "fe-web-components/feedback/types";
import { addAnnotations } from "fe-web-components/references/utils";
import { AnnotatedText } from "fe-web-components/references/AnnotatedText";
import { styled } from "@mui/material";
import { ReactComponent as NewGuidelineIcon } from "fe-web-components/assets/newGuidelineIcon.svg";
import {
  CONTACT_GUIDELINE,
  NO_GUIDELINE,
} from "fe-web-components/guidelines/types";
import DefaultButton from "./UI/DefaultButton";
import { StreamManager } from "../wsocket/wsocket";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import AnswerDialog from "./AnswerDialog";
import AnswerCitationInfo from "./AnswerCitationInfo";
import {
  ChatMessage,
  MessageType,
  QuestionType,
  QuestionTypes,
} from "../types/types";
import { References } from "fe-web-components/references/types";

//region [[ Styles ]]

//endregion [[ Styles ]]
const MessageContainer = styled("div")({
  width: "100%",
});

const ContactPopperStyled = styled("div")({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  gap: 20,
  textAlign: "center",
  marginTop: -10,
});
//endregion [[ Styles ]]

// region [[ Props ]]
export interface AnswerProps {
  message: ChatMessage;
  debugMode: boolean;
  getGuideline:
    | ((index: number, guidelineId: string) => Promise<ChatMessage>)
    | null;
  question: string;
  feedback?: ExchangeFeedback;
  historic?: boolean;
  questionType: QuestionType;
  standalone?: boolean; //answer is display outside of chat
  usedPrompt?: {
    customPrompt: string;
    predefinedInsight: PredefinedInsight;
  };
  streamManager?: StreamManager;
  onSuggestedQuestion?: () => void;
  onGuidelineChange?: (messageObject: any) => void;
  onAvatarClick?: (e) => void;
}
//endregion [[ Props ]]

//region [[ Constants ]]
const NOT_VALID_QUESTION_ANSWER_TEXT = "NOT_VALID_QUESTION_ANSWER_TEXT";
const NOT_VALID_TEXT_FALLBACK = `As a medical professional, I don't have expertise in answering your question. However, if you have any medical-related questions or need advice on patient care, I'm here to help!`;
//endregion [[ Constants ]]

//region [[ Functions ]]
// export function stripIDsFromStreamingText(str: string) {
//   if (!str) return str;
//   let newStr = str;
//   const pattern = /(?:\(source:)?\(? ?ID\.[0-9]+,?\)?/gm;
//
//   for (let match of str.matchAll(pattern)) {
//     newStr = newStr.replace(match[0], "");
//   }
//   return newStr;
// }

function stripFootnotes(text: string) {
  return text.replace(/\[\d+]/g, "");
}

//endregion [[ Functions ]]

export const Answer = ({
  message,
  debugMode,
  historic,
  standalone,
  getGuideline,
  usedPrompt,
  question,
  streamManager,
  questionType,
  onSuggestedQuestion,
  ...props
}: AnswerProps) => {
  const {
    index,
    text,
    questions,
    feedback,
    type,
    references: messageReferences,
    guidelines,
    isValid,
  } = message;
  const [answerText, setAnswerText] = useState<string>(text);
  const [references, setReferences] = useState<References>(messageReferences);
  const [guidelinesCache, setGuidelinesCache] = useState<GuidelineData[]>([]);
  const [usedGuideline, setUsedGuideline] = useState<string>(null);
  const [showMore, setShowMore] = useState<{
    citationIndex: number;
    footnoteIndex: number;
    text: string; //TODO: text will be derived from the references
  }>();
  const [showContactPopper, setShowContactPopper] = useState(false);

  const footerRef = useRef<FooterHandle>(null);
  const messageContainerRef = useRef(null);
  const messageTextContainerRef = useRef(null);
  const guidelinesListCached = useRef(false); //list of guidelines was initially cached
  const firstGuidelineCached = useRef(false); //first guideline was cached
  //region [[handle not valid text translation]]
  const { translate } = useTranslateByGender();
  let questionNotValidText = translate(NOT_VALID_QUESTION_ANSWER_TEXT);
  if (questionNotValidText === NOT_VALID_QUESTION_ANSWER_TEXT)
    questionNotValidText = NOT_VALID_TEXT_FALLBACK;
  //endregion [[handle not valid text]]

  const {
    sessionId,
    wsSessionId,
    ragSettings,
    viewSettings,
    sendFeedback,
    replyIsLoading,
  } = useSessionStore();

  // workaround for times the validation doesn't come,
  // default to true (since this means validations didn't
  // work in the rag server, and we might kill good answers)
  const valid = !ragSettings.useQuestionValidations || isValid !== false;
  const rtlDisplay = isRtl(question, ragSettings?.rtlDisplay);
  const reporter = useEventReportedContext();
  const questionId = useMemo(
    () => createQuestionId(questionType, index, usedGuideline),
    [questionType, index, usedGuideline],
  );
  const wsStream = useMemo(
    () =>
      streamManager
        ? streamManager.getStreamById(wsSessionId, questionId)
        : null,
    [streamManager, wsSessionId, questionId],
  );

  const handleSendFeedback = useCallback(
    async (
      exchangeIndex: number,
      feedbackInput: FeedbackInput,
      showThankYou: boolean,
    ) => {
      const success = await sendFeedback(
        feedbackInput,
        exchangeIndex,
        usedPrompt?.customPrompt,
        usedPrompt?.predefinedInsight,
        showThankYou,
      );
      if (success)
        reporter?.reportEvent(MIX_PANEL_EVENTS.FEEDBACK, feedbackInput);
      return success;
    },
    [reporter, sendFeedback, usedPrompt],
  );

  const onGuidelineChange = useCallback(
    async (fromId: string, toId: string) => {
      setAnswerText("");
      setUsedGuideline(toId);
      reporter?.reportEvent(MIX_PANEL_EVENTS.GUIDELINE_CHANGED, {
        from_guideline: fromId,
        to_guideline: toId,
      });
      const cachedData = guidelinesCache.find((g) => g.guideline.id === toId)
        ?.data;

      if (cachedData) {
        setAnswerText(addAnnotations(cachedData.text, cachedData.references));
        setReferences(cachedData.references);
      } else if (getGuideline) {
        //getGuideline is a function that fetches the guideline from the server -
        //It can use either answerQuestion (for the chat exchanges) or askPromptInSession (for plan/assessment)
        getGuideline(index, toId).then((guidelineMessage) => {
          if (guidelineMessage) {
            setGuidelinesCache((prev) => {
              //update the cache
              const updatedCache = [...prev];
              const cachedGuideline = updatedCache.find(
                (g) => g.guideline.id === toId,
              );
              if (cachedGuideline) cachedGuideline.data = guidelineMessage;
              return updatedCache;
            });
            setAnswerText(
              addAnnotations(
                guidelineMessage.text,
                guidelineMessage.references,
              ),
            );
            setReferences(guidelineMessage.references);
          }
        });
      }
    },
    [guidelinesCache, index, reporter, getGuideline],
  );

  const constructGuidelineList = useCallback(
    (guidelines: Guideline[]) => {
      if (
        ragSettings?.appSettings?.guidelineTriggerStrategy === "never" &&
        !guidelines?.length
      )
        return;
      const guidelinesList =
        guidelines?.map(({ __typename, ...g }) => ({
          guideline: g,
          data: null,
        })) || [];

      if (!ragSettings?.appSettings?.hideGuidelinesIgnoreButton)
        guidelinesList.push({ guideline: { id: NO_GUIDELINE }, data: null }); //add a placeholder for "no guidelines"

      // show "organizational guidelines" button if the app doesn't disable it and the app is not embedded and not in standalone mode
      if (
        !standalone &&
        !ragSettings?.embeddedView &&
        !ragSettings?.appSettings?.hideGuidelinesContactButton
      ) {
        guidelinesList.push({
          guideline: { id: CONTACT_GUIDELINE },
          data: null,
        });
      }
      guidelinesListCached.current = true;
      setGuidelinesCache(guidelinesList);
    },
    [ragSettings, standalone],
  );

  useEffect(() => {
    //reset guidelines on question change
    setGuidelinesCache([]);
    guidelinesListCached.current = false;
    firstGuidelineCached.current = false;
  }, [question]);

  useEffect(() => {
    if (type === MessageType.suggestedQuestions || replyIsLoading(questionId))
      return;

    //create the initial cache
    if (!guidelinesListCached.current) {
      constructGuidelineList(guidelines); //store the list of guidelines
    }
  }, [replyIsLoading, questionId, guidelines, type, constructGuidelineList]);

  useEffect(() => {
    if (wsStream) {
      //console.log("streaming", wsStream);
      setAnswerText(
        replaceHtmlEntities(addAnnotations(wsStream.text, wsStream.references)),
      );
      setReferences(wsStream.references);
      if (!guidelinesListCached.current && wsStream?.metadata) {
        const streamedGuidelines =
          wsStream.metadata.guidelines?.concat(
            wsStream.metadata.altGuidelines,
          ) || [];

        if (streamedGuidelines) constructGuidelineList(streamedGuidelines); //update the guidelines list while streaming (metadata is only sent at the beginning)
      }
    } else if (!replyIsLoading(questionId) && text) {
      if (
        !guidelines?.length ||
        (guidelines?.length > 0 && !firstGuidelineCached.current)
      ) {
        //update the first guideline in the cache
        if (guidelines?.length > 0 && !firstGuidelineCached.current)
          setGuidelinesCache((prevState) => {
            const updatedCache = [...prevState];
            const firstGuideline = updatedCache[0];
            firstGuideline.data = message;
            firstGuidelineCached.current = true;
            return updatedCache;
          });
        setReferences(message.references); //update the citations and footnotes
        setAnswerText(replaceHtmlEntities(addAnnotations(text, references))); //update the answer text after streaming finished and got a complete reply (!loading)
      }
    }
  }, [
    guidelines?.length,
    message,
    replyIsLoading,
    questionId,
    wsStream,
    text,
    references,
    debugMode,
    constructGuidelineList,
  ]);

  const onShowMore = useCallback(
    (citationIndex: number, footnoteIndex: number, text: string) => {
      setShowMore({
        citationIndex,
        footnoteIndex,
        text,
      });
    },
    [references],
  );

  const onFootnoteClick = useCallback((id) => {
    footerRef.current?.highlightRef(id);
  }, []);

  return (
    <>
      <Message
        className={standalone ? "standalone-custom-content" : ""}
        model={{
          payload: (
            <Message.CustomContent>
              <MessageContainer
                ref={messageContainerRef}
                dir={rtlDisplay ? "rtl" : ""}
              >
                {valid && guidelinesCache?.length > 0 && (
                  <MemoizedGuidelineIndicator
                    guidelines={guidelinesCache.map((g) => {
                      return g.guideline;
                    })}
                    onChange={(fromId, toId) => {
                      if (props.onGuidelineChange)
                        props.onGuidelineChange(messageContainerRef.current);
                      return onGuidelineChange(fromId, toId);
                    }}
                    disabled={historic} //disable guideline change
                    onShowContact={() => {
                      reporter?.reportEvent(
                        MIX_PANEL_EVENTS.GUIDELINES_REQUESTED,
                        { sessionId },
                      );
                      setShowContactPopper(true);
                    }}
                  />
                )}
                {/* If there is no yet answer text, show loading */}
                {!answerText && <ThreeDotsLoader />}
                {/* If the answer is not valid, show the default message */}
                {!!answerText && !valid && <>{questionNotValidText}</>}
                {/* If the answer is valid, show the answer text */}
                {answerText && valid && (
                  <AnnotatedText
                    streaming={!!wsStream}
                    ref={messageTextContainerRef}
                    text={answerText}
                    onFootnoteClick={onFootnoteClick}
                    onShowMore={onShowMore}
                  />
                )}
              </MessageContainer>
            </Message.CustomContent>
          ),
          sentTime: "just now",
          sender: "Kahun",
          direction: "incoming",
          position: "normal",
          type: "html",
        }}
        avatarPosition={rtlDisplay ? "tr" : "tl"}
      >
        {!standalone && (
          <Avatar
            src={
              (viewSettings.useAbsolutPaths ? RAG_ABSOLUTE_PATH : "") +
              KAHUN_AVATAR_SRC
            }
            name="Kahun"
            size={historic ? "sm" : "md"}
            onClick={props.onAvatarClick}
          />
        )}

        {!wsStream && answerText && valid && (
          <Message.Footer className={"message-footer"}>
            {type === MessageType.suggestedQuestions ? (
              questions && (
                <SuggestedQuestion
                  questions={questions}
                  onQuestionAsked={onSuggestedQuestion}
                />
              )
            ) : (
              <AnswerFooter
                citations={references.citations}
                debugMode={debugMode}
                onCopy={() =>
                  safeCopyToClipboard(
                    stripFootnotes(
                      messageTextContainerRef.current?.textContent,
                    ),
                  )
                }
                onToggleRefs={(open: boolean) => {
                  if (!open) reporter?.reportEvent(MIX_PANEL_EVENTS.REF_OPENED);
                }}
                rtlDisplay={rtlDisplay}
                feedback={feedback}
                onSendFeedback={(feedback, showThankYou) =>
                  handleSendFeedback(index, feedback, showThankYou)
                }
                ref={footerRef}
                hideFeedback={questionType === QuestionTypes.Summary}
              />
            )}
          </Message.Footer>
        )}
      </Message>
      {showMore && (
        <AnswerCitationInfo
          onClose={() => setShowMore(null)}
          text={showMore.text}
          citationIndex={showMore.citationIndex}
          footnoteIndex={showMore.footnoteIndex}
          references={references}
        />
      )}
      {showContactPopper && (
        <AnswerDialog
          onClose={() => setShowContactPopper(false)}
          maxWidth={"xs"}
        >
          <ContactPopperStyled>
            <NewGuidelineIcon style={{ height: 40, width: 40 }} />
            <div>
              Would you like to add your organizational guidelines and receive
              responses based on them?
            </div>
            <strong>
              <DefaultButton
                onClick={() => window.open("https://www.kahun.com/contact-us")}
              >
                Contact Us
              </DefaultButton>
            </strong>
          </ContactPopperStyled>
        </AnswerDialog>
      )}
    </>
  );
};

export const MemoizedAnswer = React.memo(Answer, (prev, next) => {
  //re-render only if the message text, debugMode or question changes
  return (
    prev.message?.text === next.message?.text &&
    prev.streamManager === next.streamManager &&
    prev.debugMode === next.debugMode &&
    prev.question === next.question
  );
});
